import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { z } from "zod";
import {
	INFRACTION_AIT_LENGTH,
	INFRACTION_CREATE_ALLOWED_EXTENSIONS_LABEL,
} from "../constants/infraction-create.constant";
import { isAllowedInfractionAttachmentFileName } from "../utils/infraction-attachment.utils";

dayjs.extend(customParseFormat);

const nullableNumber = z.union([z.number(), z.null()]);

const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

const ATTACHMENT_FILE_FIELDS = [
	"infractionNoticeFile",
	"penaltyNotificationFile",
	"receiptsFile",
] as const;

function validateOccurredDateTime(
	values: {
		occurredDate: string;
		occurredTime: string;
	},
	context: z.RefinementCtx
): void {
	if (!values.occurredDate) {
		return;
	}

	const occurredDate = dayjs(values.occurredDate, "YYYY-MM-DD", true);
	if (!occurredDate.isValid()) {
		return;
	}

	const today = dayjs().startOf("day");
	if (occurredDate.isAfter(today)) {
		context.addIssue({
			code: "custom",
			path: ["occurredDate"],
			message: "A data da infração não pode ser futura",
		});
		return;
	}

	const trimmedTime = values.occurredTime.trim();
	if (!(trimmedTime && TIME_24H_REGEX.test(trimmedTime))) {
		return;
	}

	if (!occurredDate.isSame(today, "day")) {
		return;
	}

	const occurredAt = dayjs(
		`${values.occurredDate} ${trimmedTime}`,
		"YYYY-MM-DD HH:mm",
		true
	);

	if (occurredAt.isValid() && occurredAt.isAfter(dayjs())) {
		context.addIssue({
			code: "custom",
			path: ["occurredTime"],
			message: "A hora não pode ser futura",
		});
	}
}

function validateAttachmentFiles(
	values: {
		infractionNoticeFile?: File | null;
		penaltyNotificationFile?: File | null;
		receiptsFile?: File | null;
	},
	context: z.RefinementCtx
): void {
	for (const field of ATTACHMENT_FILE_FIELDS) {
		const file = values[field];
		if (!file) {
			continue;
		}

		if (!isAllowedInfractionAttachmentFileName(file.name)) {
			context.addIssue({
				code: "custom",
				path: [field],
				message: `Envie apenas arquivos ${INFRACTION_CREATE_ALLOWED_EXTENSIONS_LABEL}.`,
			});
		}
	}
}

export const infractionCreateSchema = z
	.object({
		vehicleId: z.number().min(1, "Selecione um veículo"),
		ait: z.string().trim().min(1, "Informe o código do auto"),
		issuingAuthorityId: z.number().min(1, "Selecione o órgão emissor"),
		issuingAuthorityCode: z.string().optional(),
		isAnttInfraction: z.boolean(),
		occurredDate: z.string().min(1, "Informe a data"),
		occurredTime: z.string().min(1, "Informe a hora"),
		location: z.string().trim().min(1, "Informe o local"),
		infractionCode: z.string().optional(),
		breakdown: z.string().optional(),
		description: z.string().optional(),
		severity: nullableNumber.optional(),
		points: nullableNumber.optional(),
		offenderType: z.number().min(1, "Selecione a tipificação da infração"),
		originalAmount: z.number().positive("O valor deve ser maior que zero"),
		driverIndicationDeadline: z.string().optional(),
		dueDate: z.string().optional(),
		paymentDate: z.string().optional(),
		driverId: nullableNumber.optional(),
		transferPointsToDriver: z.boolean(),
		infractionNoticeFile: z.instanceof(File).nullable().optional(),
		penaltyNotificationFile: z.instanceof(File).nullable().optional(),
		receiptsFile: z.instanceof(File).nullable().optional(),
	})
	.superRefine((values, context) => {
		const expectedLength = values.isAnttInfraction
			? INFRACTION_AIT_LENGTH.ANTT
			: INFRACTION_AIT_LENGTH.STANDARD;

		if (values.ait.length !== expectedLength) {
			context.addIssue({
				code: "custom",
				path: ["ait"],
				message: values.isAnttInfraction
					? "Auto ANTT deve ter 16 caracteres"
					: "Auto comum deve ter 10 caracteres",
			});
		}

		validateOccurredDateTime(values, context);
		validateAttachmentFiles(values, context);
	});

export type InfractionCreateSchema = z.infer<typeof infractionCreateSchema>;
